import api_client from '@/repositories';
import { ICustomQueryArgsRepository } from '@/types/utils/ICustomQueryArgs';
import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

export interface IUseListResourcesQueryArgs extends ICustomQueryArgsRepository<typeof api_client.resources.list> {}

export function useListResourcesQuery(args?: IUseListResourcesQueryArgs) {
	return useQuery({
		queryKey: ['resources', 'list'],
		queryFn: api_client.resources.list,
		...args,
	});
}

export interface IIseResourceDetailsQueryArgs extends ICustomQueryArgsRepository<typeof api_client.resources.details> {
	id: string;
}

export function useResourceDetailsQuery(args: IIseResourceDetailsQueryArgs) {
	return useQuery({
		...args,
		queryKey: ['resources', 'details', args.id],
		queryFn: () => api_client.resources.details({ id: args.id }),
	});
}

interface IUseCreateResourceMutationArgs
	extends UseMutationOptions<void, unknown, Parameters<typeof api_client.resources.create>[0]> {}

export function useCreateResourceMutation(args?: IUseCreateResourceMutationArgs) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: api_client.resources.create,
		onSuccess: function (data, variables, context) {
			queryClient.invalidateQueries({ queryKey: ['resource-groups', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['resources', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['resources', 'details', variables.id] });
			/**
			 * In order to display a resource we fetch resources and groups on the same
			 * query so we need to invalidate it.
			 */
			queryClient.invalidateQueries({ queryKey: ['resources+groups', variables.id] });
			args?.onSuccess?.(data, variables, context);
		},
		...args,
	});
}

interface IUseRemoveResourceMutationArgs
	extends UseMutationOptions<void, unknown, Parameters<typeof api_client.resources.remove>[0]> {}

export function useRemoveResourceMutation(args?: IUseRemoveResourceMutationArgs) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: api_client.resources.remove,
		onSuccess: function (data, variables, context) {
			queryClient.invalidateQueries({ queryKey: ['resources', 'details', variables.id] });
			queryClient.invalidateQueries({ queryKey: ['resources', 'list'] });
			args?.onSuccess?.(data, variables, context);
		},
		...args,
	});
}

interface IUseCreateResourceMutationArgs
	extends UseMutationOptions<void, unknown, Parameters<typeof api_client.resources.create>[0]> {
	resourceGroupId: string;
}

export function useCreateResourceInGroupMutation(args: IUseCreateResourceMutationArgs) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params) => {
			api_client.resources.create(params);
			const resourceGroups = await api_client.resourceGroups.list();
			const resourceGroup = resourceGroups.resourceGroups.find((rg) => rg.id === args.resourceGroupId);

			if (!resourceGroup) {
				throw Error('Trying to create a resource in a group that does not exist');
			}

			return api_client.resourceGroups.create({
				...resourceGroup,
				resources: [...resourceGroup.resources, { id: params.id }],
			});
		},
		onSuccess: function (data, variables, context) {
			queryClient.invalidateQueries({ queryKey: ['resource-groups', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['resource-groups', 'details', args.resourceGroupId] });
			queryClient.invalidateQueries({ queryKey: ['resources', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['resources', 'details', variables.id] });
			args?.onSuccess?.(data, variables, context);
		},
		...args,
	});
}
